<template>
	<div class="itinerary-container">
		<h2 class="text-h5 mb-4 itinerary-title font-weight-bold">Itinerary</h2>
		
		<v-timeline v-if="data && data.length" density="comfortable" align="start" line-thickness="1">
			<v-timeline-item
				v-for="(item, index) in data"
				:key="index"
				dot-color="primary"
				size="x-small"
				class="timeline-item mb-4"
			>
				<template v-slot:opposite></template>
				
				<div class="timeline-content">
					<h3 class="text-h6 mb-2 font-weight-bold">{{ item.title }}</h3>
					<div class="timeline-description" v-html="item.description"></div>
					
					<!-- Image gallery only for index 1 (second item) -->
					<div v-if="index === 1 && filteredImages.length > 0" class="image-gallery mt-3">
						<div 
							v-for="(img, imgIndex) in filteredImages" 
							:key="imgIndex"
							class="gallery-image-container"
							@click="openLightbox(imgIndex)"
						>
							<v-img 
								:src="img" 
								:alt="`Itinerary image ${imgIndex + 1}`" 
								aspect-ratio="16/9"
								cover
								class="gallery-image rounded" 
								height="120"
							>
								<template v-slot:placeholder>
									<div class="d-flex align-center justify-center fill-height">
										<v-progress-circular color="primary" indeterminate size="small"></v-progress-circular>
									</div>
								</template>
								<div class="image-overlay d-flex align-center justify-center">
									<v-icon color="white" icon="mdi-magnify"></v-icon>
								</div>
							</v-img>
						</div>
					</div>
				</div>
			</v-timeline-item>
		</v-timeline>
		
		<v-alert v-else type="info" variant="outlined" density="compact" class="mt-2">
			No itinerary information available
		</v-alert>
		
		<!-- Enhanced Lightbox -->
		<v-dialog
			v-model="showLightbox"
			fullscreen
			transition="dialog-bottom-transition"
			:scrim="true"
			content-class="lightbox-dialog"
		>
			<v-card class="lightbox-container">
				<!-- Toolbar with close and fullscreen buttons -->
				<v-toolbar density="compact" color="rgba(0,0,0,0.7)" class="lightbox-toolbar">
					<span class="text-caption text-white">{{ currentIndex + 1 }} / {{ filteredImages.length }}</span>
					<v-spacer></v-spacer>
					
					<v-btn 
						icon="mdi-fullscreen" 
						variant="text" 
						color="white" 
						@click="toggleFullscreen"
						class="mr-2"
					></v-btn>
					
					<v-btn 
						icon="mdi-close" 
						variant="text" 
						color="white" 
						@click="closeLightbox"
					></v-btn>
				</v-toolbar>
				
				<!-- Main image carousel -->
				<v-card-text class="pa-0 fill-height d-flex flex-column">
					<v-carousel
						v-model="currentIndex"
						hide-delimiters
						:show-arrows="false"
						height="100%"
						class="lightbox-carousel"
						touch
						continuous
						:cycle="false"
					>
						<v-carousel-item
							v-for="(img, index) in filteredImages"
							:key="index"
							class="lightbox-carousel-item"
							:transition="'fade-transition'"
							:reverse-transition="'fade-transition'"
						>
							<div class="image-container d-flex align-center justify-center">
								<v-img
									:src="img"
									:alt="`Itinerary image ${index + 1}`"
									class="lightbox-image"
									:class="{ 'fullscreen-mode': isFullscreen }"
									contain
									max-height="90vh"
									eager
								>
									<template v-slot:placeholder>
										<div class="d-flex align-center justify-center fill-height">
											<v-progress-circular indeterminate color="primary"></v-progress-circular>
										</div>
									</template>
								</v-img>
							</div>
						</v-carousel-item>
					</v-carousel>
					
					<!-- Navigation buttons -->
					<div class="navigation-controls">
						<v-btn
							icon
							variant="text"
							color="white"
							size="large"
							class="nav-button prev-button"
							@click="prevImage"
							:disabled="currentIndex === 0"
						>
							<v-icon>mdi-chevron-left</v-icon>
						</v-btn>
						
						<v-btn
							icon
							variant="text"
							color="white"
							size="large"
							class="nav-button next-button"
							@click="nextImage"
							:disabled="currentIndex === filteredImages.length - 1"
						>
							<v-icon>mdi-chevron-right</v-icon>
						</v-btn>
					</div>
					
					<!-- Thumbnails row -->
					<div class="thumbnails-container pa-2">
						<div 
							v-for="(img, index) in filteredImages" 
							:key="`thumb-${index}`"
							class="thumbnail-item"
							:class="{ 'active': index === currentIndex }"
							@click="goToSlide(index)"
						>
							<v-img 
								:src="img" 
								:alt="`Thumbnail ${index + 1}`" 
								width="50" 
								height="35"
								cover
								class="thumbnail-image"
							></v-img>
						</div>
					</div>
				</v-card-text>
			</v-card>
		</v-dialog>
	</div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';

const props = defineProps({
	data: {
		type: Array,
		default: () => [],
	},
	images: {
		type: Array,
		default: () => [],
	},
});

// Filter images specifically for the itinerary - only show tour-related images
const filteredImages = computed(() => {
	if (!props.images || props.images.length === 0) {
		return [];
	}
	
	// Filter images that are likely to be itinerary-related
	// Look for itinerary-related keywords in image paths or alt text
	const itineraryImages = props.images.filter(img => {
		// Check if the image path contains keywords related to tours/attractions
		const imgPath = img.toLowerCase();
		return imgPath.includes('tour') || 
					 imgPath.includes('attraction') || 
					 imgPath.includes('itinerary') ||
					 imgPath.includes('activity') ||
					 imgPath.includes('island') ||
					 imgPath.includes('destination');
	});
	
	// Always limit to 5 images maximum
	return itineraryImages.length ? itineraryImages.slice(0, 5) : props.images.slice(0, 5);
});

// Lightbox state
const showLightbox = ref(false);
const currentIndex = ref(0);
const isFullscreen = ref(false);

// Open lightbox with specified image index
const openLightbox = (index) => {
	currentIndex.value = index;
	showLightbox.value = true;
	document.body.classList.add('lightbox-open'); // Prevent body scrolling
};

// Close lightbox
const closeLightbox = () => {
	showLightbox.value = false;
	document.body.classList.remove('lightbox-open');
};

// Image navigation
const nextImage = () => {
	if (currentIndex.value < filteredImages.value.length - 1) {
		currentIndex.value++;
	}
};

const prevImage = () => {
	if (currentIndex.value > 0) {
		currentIndex.value--;
	}
};

// Go to specific slide
const goToSlide = (index) => {
    // Force update by resetting and then setting the value
    const targetIndex = index;
    currentIndex.value = -1;
    // Use setTimeout to break the reactivity chain and ensure the update gets processed
    setTimeout(() => {
        currentIndex.value = targetIndex;
    }, 10);
};

// Toggle fullscreen mode
const toggleFullscreen = () => {
	isFullscreen.value = !isFullscreen.value;
};

// Keyboard navigation
const handleKeyDown = (event) => {
	if (!showLightbox.value) return;
	
	switch (event.key) {
		case 'ArrowLeft':
			prevImage();
			break;
		case 'ArrowRight':
			nextImage();
			break;
		case 'Escape':
			closeLightbox();
			break;
		case 'f':
			toggleFullscreen();
			break;
	}
};

// Add and remove event listeners
onMounted(() => {
	window.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
	window.removeEventListener('keydown', handleKeyDown);
	document.body.classList.remove('lightbox-open');
});

// Close lightbox when navigating away
watch(() => props.data, () => {
	closeLightbox();
});
</script>

<style scoped>
.itinerary-container {
	width: 100%;
}

.itinerary-title {
	border-bottom: 1px solid var(--v-primary-base, #1867c0);
	padding-bottom: 8px;
	margin-bottom: 16px;
	letter-spacing: 0.5px;
}

.timeline-content {
	padding: 12px;
	border-radius: 0;
	box-shadow: none;
	border: none;
	margin-left: 8px;
}

.timeline-description {
	line-height: 1.6;
}

.timeline-description :deep(p) {
	margin-bottom: 8px;
}

.image-gallery {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}

.gallery-image-container {
	flex: 0 0 calc(33.333% - 6px);
	cursor: pointer;
	transition: transform 0.2s ease;
	min-width: 100px;
	position: relative;
}

.gallery-image-container:hover {
	transform: scale(1.02);
}

.gallery-image {
	transition: opacity 0.2s ease;
}

.image-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.3);
	opacity: 0;
	transition: opacity 0.2s ease;
}

.gallery-image-container:hover .image-overlay {
	opacity: 1;
}

/* Lightbox styles */
.lightbox-dialog {
	background-color: rgba(0,0,0,0.95);
}

.lightbox-container {
	background-color: transparent;
	height: 100%;
	display: flex;
	flex-direction: column;
	border: none;
}

.lightbox-toolbar {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	z-index: 10;
}

.lightbox-carousel {
	flex: 1;
    position: relative;
}

.lightbox-carousel-item {
	display: flex !important;
	align-items: center !important;
	justify-content: center !important;
	height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
}

.image-container {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
}

.lightbox-image {
	max-width: 95vw;
	transition: all 0.3s ease;
	margin: 0 auto;
}

.lightbox-image.fullscreen-mode {
	max-height: 100vh !important;
	max-width: 100vw !important;
}

.navigation-controls {
	position: absolute;
	top: 50%;
	left: 0;
	right: 0;
	transform: translateY(-50%);
	display: flex;
	justify-content: space-between;
	padding: 0 12px;
	pointer-events: none;
}

.nav-button {
	background-color: rgba(0,0,0,0.3) !important;
	pointer-events: auto;
}

.thumbnails-container {
	display: flex;
	overflow-x: auto;
	gap: 6px;
	background-color: rgba(0,0,0,0.7);
	padding: 6px;
}

.thumbnail-item {
	cursor: pointer;
	opacity: 0.5;
	transition: all 0.2s ease;
	border: 2px solid transparent;
    margin: 0 2px;
}

.thumbnail-item:hover {
	opacity: 0.8;
    transform: scale(1.1);
}

.thumbnail-item.active {
	opacity: 1;
	border-color: var(--v-primary-base, #1867c0);
    transform: scale(1.1);
}

.thumbnail-image {
	border-radius: 2px;
}

/* Body style when lightbox is open */
:global(body.lightbox-open) {
	overflow: hidden;
}

@media (max-width: 600px) {
	.gallery-image-container {
		flex: 0 0 calc(50% - 4px);
	}
	
	.thumbnails-container {
		padding: 3px;
	}
	
	.thumbnail-item {
		width: 36px;
		height: 24px;
	}
	
	.timeline-content {
		padding: 8px;
	}
}

:deep(.v-carousel__item) {
	display: flex !important;
	align-items: center !important;
	justify-content: center !important;
}

:deep(.v-carousel__item--active) {
    z-index: 1;
}

:deep(.v-img__img) {
	object-position: center center !important;
}

:deep(.fade-transition-enter-active),
:deep(.fade-transition-leave-active) {
    transition: opacity 0.3s ease;
}

:deep(.fade-transition-enter-from),
:deep(.fade-transition-leave-to) {
    opacity: 0;
}

:deep(.fade-transition-leave-from),
:deep(.fade-transition-enter-to) {
    opacity: 1;
}
</style>
